class CroGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cro-gallery__track');
    this.slides = Array.from(this.querySelectorAll('.cro-gallery__slide'));
    this.dots = Array.from(this.querySelectorAll('.cro-gallery__dot'));
    this.thumbsEl = this.querySelector('.cro-gallery__thumbs');
    this.thumbs = Array.from(this.querySelectorAll('.cro-gallery__thumb'));
    this.offset = parseInt(this.dataset.thumbOffset || '0', 10);
    this.prevArrow = this.querySelector('.cro-gallery__arrow--prev');
    this.nextArrow = this.querySelector('.cro-gallery__arrow--next');
    this.count = this.querySelector('.cro-gallery__count');
    if (!this.track || !this.slides.length) return;
    this.desktop = matchMedia('(width >= 64.0625rem)');
    this.onBreakpointChange = () => {
      clearTimeout(this.trackTimer);
      this.updateSlides();
      this.scrollTrackTo(this.current, 'instant');
    };
    this.desktop.addEventListener('change', this.onBreakpointChange);

    this.dragged = false;
    this.windowStart = 0;
    this.autoScrollUntil = 0;
    this.targetLockUntil = 0;
    this.current = undefined;
    this.setActive(0);

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goTo(i);
      });
    });
    if (this.prevArrow) this.prevArrow.addEventListener('click', () => this.goTo(this.target - 1));
    if (this.nextArrow) this.nextArrow.addEventListener('click', () => this.goTo(this.target + 1));
    this.thumbs.forEach((thumb) => {
      const i = parseInt(thumb.dataset.index || '0', 10);
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.dragged) this.goTo(i);
      });
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goTo(this.target + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goTo(this.target - 1);
      }
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.desktop.matches) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const i = this.slides.indexOf(entry.target);
            if (i > -1) this.setActive(i);
          }
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));

    this.enableDrag(this.track);
    this.track.addEventListener('scroll', () => {
      if (this.desktop.matches || this.slides.length < 2) return;
      clearTimeout(this.trackTimer);
      this.trackTimer = setTimeout(() => {
        const width = this.slides[1].offsetLeft - this.slides[0].offsetLeft;
        const index = Math.max(0, Math.min(Math.round(this.track.scrollLeft / width), this.slides.length - 1));
        if (index !== this.target && performance.now() < this.targetLockUntil) return this.scrollTrackTo(this.target);
        this.setActive(index);
        this.revealThumb(this.current);
      }, 150);
    }, { passive: true });
    ['pointerdown', 'touchstart', 'wheel'].forEach((type) =>
      this.track.addEventListener(type, () => (this.targetLockUntil = 0), { passive: true })
    );
    if (!this.thumbsEl) return;

    this.enableDrag(this.thumbsEl);
    this.thumbsEl.addEventListener('scroll', () => this.queueWindowSync(), { passive: true });
    ['pointerdown', 'touchstart', 'wheel'].forEach((type) =>
      this.thumbsEl.addEventListener(type, () => (this.autoScrollUntil = 0), { passive: true })
    );
    this.resizeObserver = new ResizeObserver(() => {
      this.windowStart = this.scrolledStart;
      this.revealThumb(this.current);
    });
    this.resizeObserver.observe(this.thumbsEl);
    this.syncOverflow();
  }

  disconnectedCallback() {
    this.desktop?.removeEventListener('change', this.onBreakpointChange);
    if (this.observer) this.observer.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    clearTimeout(this.settleTimer);
    clearTimeout(this.trackTimer);
    clearTimeout(this.fadeTimer);
  }

  get scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  }

  get thumbPitch() {
    if (this.thumbs.length < 2) return 0;
    return this.thumbs[1].getBoundingClientRect().left - this.thumbs[0].getBoundingClientRect().left;
  }

  get visibleThumbs() {
    const pitch = this.thumbPitch;
    if (!pitch) return this.thumbs.length;
    return Math.max(1, Math.round((this.thumbsEl.clientWidth + pitch - this.thumbs[0].offsetWidth) / pitch));
  }

  get scrolledStart() {
    const pitch = this.thumbPitch;
    return pitch ? Math.round(Math.abs(this.thumbsEl.scrollLeft) / pitch) : 0;
  }

  queueWindowSync() {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      if (Math.abs(this.thumbsEl.scrollLeft - this.windowStart * this.thumbPitch) < 2) return;
      if (performance.now() < this.autoScrollUntil) return this.revealThumb(this.current);
      this.windowStart = this.scrolledStart;
      this.syncOverflow();
    }, 120);
  }

  syncOverflow() {
    if (!this.thumbsEl || !this.thumbs.length) return;
    const last = this.windowStart + this.visibleThumbs - 1;
    const index = last < this.thumbs.length - 1 && last !== this.current - this.offset ? last : -1;
    const remaining = index < 0 ? 0 : this.thumbs.length - 1 - index + this.offset;
    this.thumbs.forEach((thumb, i) => {
      const more = thumb.querySelector('.cro-gallery__thumb-more');
      const on = i === index;
      if (more) {
        more.hidden = !on;
        if (on) more.textContent = `+${remaining}`;
      }
      thumb.setAttribute('aria-label', on ? `Image ${i + 1 + this.offset}, ${remaining} more` : `Image ${i + 1 + this.offset}`);
    });
  }

  enableDrag(el) {
    let startX = 0;
    let startLeft = 0;
    let active = false;
    el.addEventListener('dragstart', (e) => e.preventDefault());
    el.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || (el === this.track && this.desktop.matches)) return;
      active = true;
      this.dragged = false;
      startX = e.clientX;
      startLeft = el.scrollLeft;
    });
    el.addEventListener('pointermove', (e) => {
      if (!active || !(e.buttons & 1)) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) <= 6 && !this.dragged) return;
      if (!this.dragged) {
        this.dragged = true;
        el.style.scrollSnapType = 'none';
        el.setPointerCapture(e.pointerId);
      }
      el.scrollLeft = startLeft - dx;
    });
    const release = (e) => {
      if (!active) return;
      active = false;
      el.style.scrollSnapType = '';
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    };
    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', (e) => {
      release(e);
      this.dragged = false;
    });
    el.addEventListener(
      'click',
      (e) => {
        if (!this.dragged) return;
        e.preventDefault();
        e.stopPropagation();
        this.dragged = false;
      },
      true
    );
  }

  goTo(index) {
    const i = Math.max(0, Math.min(index, this.slides.length - 1));
    this.target = i;
    this.targetLockUntil = performance.now() + 1000;
    if (this.desktop.matches) {
      this.setActive(i);
      return;
    }
    this.scrollTrackTo(i);
  }

  scrollTrackTo(i, behavior = this.scrollBehavior) {
    this.track.scrollTo({ left: this.slides[i].offsetLeft - this.slides[0].offsetLeft, behavior });
  }

  setActive(index) {
    if (index === this.current) return;
    this.current = index;
    this.updateSlides();
    if (index === this.target || performance.now() > this.targetLockUntil) this.target = index;
    const mark = (el, on) => (on ? el.setAttribute('aria-current', 'true') : el.removeAttribute('aria-current'));
    this.dots.forEach((dot, i) => mark(dot, i === index));
    this.thumbs.forEach((thumb, i) => mark(thumb, i + this.offset === index));
    if (this.prevArrow) this.prevArrow.disabled = index === 0;
    if (this.nextArrow) this.nextArrow.disabled = index === this.slides.length - 1;
    if (this.count) this.count.textContent = `${index + 1} / ${this.slides.length}`;
    this.revealThumb(index);
  }

  updateSlides() {
    this.slides.forEach((slide, index) => (slide.inert = this.desktop.matches && index !== this.current));
    this.slides[this.current].classList.remove('lg:hidden');
    this.slides[this.current].getBoundingClientRect();
    this.slides.forEach((slide, index) => slide.classList.toggle('cro-gallery--active', index === this.current));
    clearTimeout(this.fadeTimer);
    this.fadeTimer = setTimeout(() => {
      this.slides.forEach((slide, index) => slide.classList.toggle('lg:hidden', index !== this.current));
    }, 200);
  }

  revealThumb(slideIndex) {
    const index = Math.max(slideIndex - this.offset, 0);
    if (!this.thumbsEl || !this.thumbs[index]) return;
    const count = this.visibleThumbs;
    const lastIndex = this.thumbs.length - 1;
    let start = this.windowStart;
    if (index < start) start = index;
    else if (index >= start + count - 1) start = index < lastIndex ? index - count + 2 : index - count + 1;
    this.windowStart = Math.min(Math.max(start, 0), Math.max(lastIndex + 1 - count, 0));
    const left = this.windowStart * this.thumbPitch;
    if (Math.abs(this.thumbsEl.scrollLeft - left) > 1) {
      this.autoScrollUntil = performance.now() + 1000;
      this.thumbsEl.scrollTo({ left, behavior: 'instant' });
    }
    this.syncOverflow();
  }
}

if (!customElements.get('cro-gallery')) customElements.define('cro-gallery', CroGallery);
